import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAppStore } from '../stores/useAppStore';
import type { LoginFormData, LoginResponse } from '../types';
import { sanitizeInput, validateUsername, validatePassword, loginRateLimiter } from '../utils/security';
import { STRINGS } from '../utils/constants';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAppStore();
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
    schoolCode: '',
  });
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [rateLimitError, setRateLimitError] = useState<string>('');
  const [remainingAttempts, setRemainingAttempts] = useState<number>(5);
  const [tenants, setTenants] = useState<Array<{ schoolCode: string; schoolName?: string | null }>>([]);
  const [rememberSchool, setRememberSchool] = useState<boolean>(true);

  // Update remaining attempts on component mount
  useEffect(() => {
    const attempts = loginRateLimiter.getRemainingAttempts('login');
    setRemainingAttempts(attempts);
  }, []);

  // Load tenants for dropdown and restore remembered school
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await apiService.get<{ success: boolean; tenants?: Array<{ schoolCode: string; schoolName?: string | null }> }>('/api/tenants');
        if (!mounted) return;
        if (resp?.success && Array.isArray(resp.tenants)) {
          setTenants(resp.tenants);
        }
      } catch {
        // ignore - backend might be down during initial load
      }
    })();

    try {
      const savedSchoolCode = localStorage.getItem('schoolCode');
      if (savedSchoolCode) {
        setFormData(prev => ({ ...prev, schoolCode: savedSchoolCode }));
      }
    } catch {
      // ignore
    }
    return () => {
      mounted = false;
    };
  }, []);

  // Form validation with enhanced security
  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};

    // Sanitize inputs
    const sanitizedUsername = sanitizeInput(formData.username);
    const sanitizedPassword = sanitizeInput(formData.password);

    // Validate username
    const usernameValidation = validateUsername(sanitizedUsername);
    if (!usernameValidation.isValid) {
      newErrors.username = usernameValidation.error;
    }

    // Validate password
    const passwordValidation = validatePassword(sanitizedPassword);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes with sanitization
  const handleInputChange = (field: keyof LoginFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const sanitizedValue = sanitizeInput(event.target.value);
    
    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue,
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
    
    // Clear error from store
    if (error) {
      clearError();
    }
    
    // Clear rate limit error
    if (rateLimitError) {
      setRateLimitError('');
    }
  };

  // Handle form submission with rate limiting and security
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Check rate limiting
    if (!loginRateLimiter.isAllowed('login')) {
      setRateLimitError(STRINGS.LOGIN.ERRORS.RATE_LIMITED);
      const attempts = loginRateLimiter.getRemainingAttempts('login');
      setRemainingAttempts(attempts);
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    clearError();
    setRateLimitError('');

    // Sanitize inputs before sending
    const sanitizedData = {
      username: sanitizeInput(formData.username),
      password: sanitizeInput(formData.password),
      schoolCode: sanitizeInput(formData.schoolCode || ''),
    };

    // Always send schoolCode if selected, backend will detect super admin automatically
    const schoolCodeToSend = sanitizedData.schoolCode || null;

    // Persist school selection (no password storage in app)
    try {
      if (rememberSchool && schoolCodeToSend) {
        localStorage.setItem('schoolCode', schoolCodeToSend);
        const matched = tenants.find(t => t.schoolCode === schoolCodeToSend);
        if (matched?.schoolName) localStorage.setItem('schoolName', matched.schoolName);
      }
      if (!rememberSchool) {
        localStorage.removeItem('schoolCode');
        localStorage.removeItem('schoolName');
      }
    } catch {
      // ignore
    }

    const success = await login(sanitizedData.username, sanitizedData.password, schoolCodeToSend);
    
    if (success) {
      // Navigate based on user type
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      if (user?.isSuperAdmin) {
        navigate('/admin/tenants');
      } else {
        navigate('/dashboard');
      }
    } else {
      // Update rate limiting
      const attempts = loginRateLimiter.getRemainingAttempts('login');
      setRemainingAttempts(attempts);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
        role="main"
        aria-label="Đăng nhập vào hệ thống"
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 400,
          }}
          role="form"
          aria-label="Form đăng nhập"
        >
          <Typography component="h1" variant="h4" gutterBottom>
            {STRINGS.LOGIN.TITLE}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {STRINGS.LOGIN.SUBTITLE}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                role="alert"
                aria-live="polite"
              >
                {error}
              </Alert>
            )}

            {rateLimitError && (
              <Alert 
                severity="warning" 
                sx={{ mb: 2 }}
                role="alert"
                aria-live="polite"
              >
                {rateLimitError}
                {remainingAttempts > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Còn lại {remainingAttempts} lần thử.
                  </Typography>
                )}
              </Alert>
            )}

            <Autocomplete
              disablePortal
              options={tenants}
              value={tenants.find(t => t.schoolCode === (formData.schoolCode || '')) || null}
              onChange={(_, value) => {
                setFormData(prev => ({ ...prev, schoolCode: value?.schoolCode || '' }));
              }}
              getOptionLabel={(opt) => (opt.schoolName ? `${opt.schoolName} (${opt.schoolCode})` : opt.schoolCode)}
              isOptionEqualToValue={(option, value) => option.schoolCode === value.schoolCode}
              disabled={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Trường (tuỳ chọn cho Super Admin)"
                  placeholder="Gõ để tìm trường..."
                />
              )}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={STRINGS.LOGIN.USERNAME_LABEL}
              name="username"
              autoComplete="username"
              autoFocus
              placeholder="Tên đăng nhập, Email hoặc Sđt"
              value={formData.username}
              onChange={handleInputChange('username')}
              error={!!errors.username}
              helperText={errors.username}
              disabled={isLoading}
              inputProps={{
                'aria-describedby': errors.username ? 'username-error' : undefined,
                'aria-invalid': !!errors.username,
                maxLength: 50,
              }}
              FormHelperTextProps={{
                id: 'username-error',
                role: errors.username ? 'alert' : undefined,
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={STRINGS.LOGIN.PASSWORD_LABEL}
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              disabled={isLoading}
              inputProps={{
                'aria-describedby': errors.password ? 'password-error' : undefined,
                'aria-invalid': !!errors.password,
                maxLength: 128,
              }}
              FormHelperTextProps={{
                id: 'password-error',
                role: errors.password ? 'alert' : undefined,
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, minHeight: 48 }}
              disabled={isLoading || !!rateLimitError}
              aria-describedby={rateLimitError ? 'rate-limit-warning' : undefined}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  {STRINGS.LOGIN.LOADING}
                </>
              ) : (
                STRINGS.LOGIN.SUBMIT_BUTTON
              )}
            </Button>

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberSchool}
                  onChange={(e) => setRememberSchool(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Nhớ trường"
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
